import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  FaUserCircle,
  FaThumbsUp,
  FaThumbsDown,
  FaRegStar,
  FaStar,
  FaPlus,
  FaCheck,
  FaVideo,
  FaVolumeUp,
  FaLock,
  FaQuestionCircle,
  FaInfoCircle,
} from "react-icons/fa";
import {
  likeMode,
  dislikeMode,
  addModeById,
  removeModeFromUser,
  toggleCustomModeFavorite,
} from "../../api/custom-mode";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { queueEvent } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { Button, Modal } from "../../ui";

// Server-assigned badges (at most two per mode).
const BADGES = {
  staff_pick: { label: "اختيار الفريق", className: "bg-primary text-header" },
  trending: { label: "رائجة", className: "bg-mainButton text-white" },
  rising: { label: "صاعدة", className: "bg-success/20 text-success" },
  new: { label: "جديدة", className: "bg-primary/15 text-primary" },
  most_completed: { label: "تُلعب للنهاية", className: "bg-success text-white" },
  hard: { label: "صعبة", className: "bg-danger text-white" },
  rich_media: { label: "صوت وفيديو", className: "bg-white/15 text-white" },
};

// Why a mode is in the "for you" rail.
const reasonText = (reason) => {
  switch (reason?.type) {
    case "similar_to":
      return reason.refName ? `لأنك أحببت «${reason.refName}»` : "مشابهة لما تحب";
    case "followed_creator":
      return "من صانع تتابعه";
    case "category":
      return "من تصنيف تحبه";
    case "popular_in_country":
      return "رائجة في بلدك";
    default:
      return null;
  }
};

// Community / profile mode card, matching the app's PublishedModeCard:
// owner header + image overlays (badges) + name + favorite/add.
// `inProfile` hides the owner header (already on that owner's profile).
const PublishedModeCard = ({ mode, inProfile = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isFreeTier, isLimitReached, showUpgradeModal } = useSubscription();

  const [isLiked, setIsLiked] = useState(!!mode.isLiked);
  const [isDisliked, setIsDisliked] = useState(!!mode.isDisliked);
  const [likes, setLikes] = useState(mode.likesCount || 0);
  const [dislikes, setDislikes] = useState(mode.dislikesCount || 0);
  const [isAdded, setIsAdded] = useState(!!mode.isAdded);
  const [isFavorited, setIsFavorited] = useState(!!mode.isFavorited);
  const [popup, setPopup] = useState(null); // "questions" | "media" | "info"

  const owner = typeof mode.owner === "object" ? mode.owner : null;
  const ownerName = owner?.gameName || owner?.name || "";
  const ownerRole = owner?.specialBadges?.[0]?.text || "";
  // The app rings the avatar with the owner's badge colour (gold for premium),
  // falling back to a faint border otherwise.
  const ownerColor = owner?.specialBadges?.[0]?.color || "rgba(255,255,255,0.3)";

  const hasMedia =
    mode.videoQuestionsCount > 0 || mode.audioQuestionsCount > 0;

  // Reactions: update at once, send the state we want (not a toggle), then
  // adopt the server's counts — or roll back if the request fails.
  const applyServer = (res) => {
    if (typeof res?.likesCount === "number") setLikes(res.likesCount);
    if (typeof res?.dislikesCount === "number") setDislikes(res.dislikesCount);
  };
  const react = async (next) => {
    const before = { isLiked, isDisliked, likes, dislikes };
    const current = isLiked ? "like" : isDisliked ? "dislike" : "none";
    setIsLiked(next === "like");
    setIsDisliked(next === "dislike");
    setLikes((c) => Math.max(0, c + (next === "like") - (current === "like")));
    setDislikes((c) => Math.max(0, c + (next === "dislike") - (current === "dislike")));
    try {
      let res;
      if (next === "like") {
        // Older servers don't clear a dislike when liking.
        if (current === "dislike") await dislikeMode(mode._id, false);
        res = await likeMode(mode._id, true);
      } else if (next === "dislike") {
        res = await dislikeMode(mode._id, true);
      } else if (current === "like") {
        res = await likeMode(mode._id, false);
      } else {
        res = await dislikeMode(mode._id, false);
      }
      applyServer(res);
    } catch (err) {
      setIsLiked(before.isLiked);
      setIsDisliked(before.isDisliked);
      setLikes(before.likes);
      setDislikes(before.dislikes);
      toast.error(
        err?.response?.status === 429 ? "محاولات كثيرة، انتظر قليلاً" : "تعذّر تسجيل تقييمك",
      );
    }
  };

  const favMut = useMutation({
    mutationFn: (next) => toggleCustomModeFavorite(mode._id, next),
    onError: () => {
      setIsFavorited((v) => !v);
      toast.error("تعذّر تحديث المفضلة");
    },
  });
  const addMut = useMutation({
    mutationFn: () => addModeById(mode._id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["myCustomModes"] }),
    onError: (e) => {
      // "Already added" means the server has what we want.
      if (e?.response?.status === 400) return;
      setIsAdded(false);
      toast.error(e?.response?.data?.message || "تعذّرت الإضافة");
    },
  });
  const removeMut = useMutation({
    mutationFn: () => removeModeFromUser(mode._id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["myCustomModes"] }),
    onError: () => {
      setIsAdded(true);
      toast.error("تعذّرت الإزالة");
    },
  });

  const handleLike = (e) => {
    e.stopPropagation();
    react(isLiked ? "none" : "like");
  };
  const handleDislike = (e) => {
    e.stopPropagation();
    react(isDisliked ? "none" : "dislike");
  };
  const handleFavorite = (e) => {
    e.stopPropagation();
    const next = !isFavorited;
    setIsFavorited(next);
    favMut.mutate(next);
  };
  const handleAdd = (e) => {
    e.stopPropagation();
    if (mode.isOwner) return;
    if (isAdded) {
      setIsAdded(false);
      removeMut.mutate();
      return;
    }
    const count = (queryClient.getQueryData(["myCustomModes"]) || []).length;
    if (isFreeTier && isLimitReached("MAX_CUSTOM_MODES", count)) {
      showUpgradeModal({ messageKey: "CUSTOM_MODES_LIMIT" });
      return;
    }
    setIsAdded(true);
    addMut.mutate();
  };

  const openPopup = (type) => (e) => {
    e.stopPropagation();
    setPopup(type);
  };
  const openDetail = () => {
    queueEvent(EVENTS.COMMUNITY_MODE_OPENED, { modeId: mode._id });
    navigate(`/app/modes/${mode._id}`);
  };
  const openOwner = (e) => {
    e.stopPropagation();
    if (owner?._id) navigate(`/app/users/${owner._id}`);
  };

  return (
    <div className="h-full flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-white/[0.05] hover:border-white/20 transition-colors">
      {!inProfile && owner && (
        <button
          type="button"
          onClick={openOwner}
          className="w-full flex items-center gap-2 p-2.5 hover:bg-white/[0.04] transition-colors"
        >
          {owner.image ? (
            <img
              src={owner.image}
              alt=""
              className="w-8 h-8 rounded-full object-cover border-2"
              style={{ borderColor: ownerColor }}
            />
          ) : (
            <FaUserCircle className="w-8 h-8 text-white/30" />
          )}
          <div className="text-right leading-tight min-w-0">
            <p className="text-white text-sm font-semibold line-clamp-1">
              {ownerName}
            </p>
            {ownerRole && (
              <p className="text-white/55 text-[11px] line-clamp-1">{ownerRole}</p>
            )}
          </div>
        </button>
      )}

      {/* Image + overlays */}
      <button
        type="button"
        onClick={openDetail}
        className="relative block w-full aspect-square bg-white/[0.06]"
      >
        {mode.image ? (
          <img src={mode.image} alt={mode.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-white/[0.06]" />
        )}

        {/* top-left: info + lock */}
        <div className="absolute top-2 left-2 flex gap-1">
          {mode.info && (
            <span
              role="button"
              onClick={openPopup("info")}
              className="bg-header/80 backdrop-blur-sm text-white p-1.5 rounded-lg flex items-center hover:bg-header"
              aria-label="معلومات"
            >
              <FaInfoCircle size={12} />
            </span>
          )}
          {mode.locked && mode.isOwner && (
            <span className="bg-header/80 backdrop-blur-sm text-primary p-1.5 rounded-lg flex items-center">
              <FaLock size={11} />
            </span>
          )}
        </div>

        {/* top-right: media (tap → breakdown) */}
        {hasMedia && (
          <span
            role="button"
            onClick={openPopup("media")}
            className="absolute top-2 right-2 bg-header/80 backdrop-blur-sm text-primary px-2 py-1.5 rounded-lg flex items-center gap-1 hover:bg-header"
            aria-label="أسئلة الوسائط"
          >
            {mode.videoQuestionsCount > 0 && <FaVideo size={11} />}
            {mode.audioQuestionsCount > 0 && <FaVolumeUp size={11} />}
          </span>
        )}

        {/* bottom-left: like / dislike */}
        <div className="absolute bottom-2 left-2 flex gap-1">
          <span
            role="button"
            onClick={handleLike}
            className="bg-header/80 backdrop-blur-sm text-white text-xs font-bold rounded-lg px-2 py-1 flex items-center gap-1 hover:bg-header"
          >
            <FaThumbsUp className={isLiked ? "text-success" : ""} size={11} />
            {likes}
          </span>
          <span
            role="button"
            onClick={handleDislike}
            className="bg-header/80 backdrop-blur-sm text-white text-xs font-bold rounded-lg px-2 py-1 flex items-center gap-1 hover:bg-header"
          >
            <FaThumbsDown className={isDisliked ? "text-danger" : ""} size={11} />
            {dislikes}
          </span>
        </div>

        {/* bottom-right: question count (tap → breakdown) */}
        <span
          role="button"
          onClick={openPopup("questions")}
          className="absolute bottom-2 right-2 bg-header/80 backdrop-blur-sm text-white text-xs font-bold rounded-lg px-2 py-1 flex items-center gap-1 hover:bg-header"
          aria-label="عدد الأسئلة"
        >
          {mode.questionsCount ?? 0}
          <FaQuestionCircle size={11} />
        </span>
      </button>

      {/* name + actions */}
      <div className="p-3 flex-1 flex flex-col">
        <h3 className="text-white font-bold line-clamp-1 mb-1">{mode.name}</h3>
        {reasonText(mode.reason) && (
          <p className="text-primary text-xs line-clamp-1 mb-1">
            {reasonText(mode.reason)}
          </p>
        )}
        {(mode.badges || []).some((b) => BADGES[b]) && (
          <div className="flex flex-wrap gap-1 mb-2">
            {mode.badges
              .filter((b) => BADGES[b])
              .slice(0, 2)
              .map((b) => (
                <span
                  key={b}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${BADGES[b].className}`}
                >
                  {BADGES[b].label}
                </span>
              ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-auto">
          <button
            type="button"
            onClick={handleFavorite}
            className="w-9 h-9 shrink-0 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 hover:text-white flex items-center justify-center transition-colors"
            aria-label="مفضلة"
            aria-pressed={isFavorited}
          >
            {isFavorited ? (
              <FaStar className="text-primary" />
            ) : (
              <FaRegStar />
            )}
          </button>
          {!mode.isOwner && (
            <button
              type="button"
              onClick={handleAdd}
              className={`flex-1 h-9 text-sm font-bold rounded-lg flex items-center justify-center gap-1.5 transition-colors ${
                isAdded
                  ? "bg-white/10 hover:bg-white/15 text-white/70"
                  : "bg-mainButton hover:bg-mainButtonShadow text-white"
              }`}
            >
              {isAdded ? <FaCheck /> : <FaPlus />}
              {isAdded ? "مُضافة" : "إضافة"}
            </button>
          )}
        </div>
      </div>

      <Modal
        open={!!popup}
        onClose={() => setPopup(null)}
        size="sm"
        title={<span className="line-clamp-1">{mode.name}</span>}
      >
        {popup === "questions" && (
          <ul className="divide-y divide-white/10 text-white/80">
            <li className="flex justify-between py-2">
              <span>إجمالي الأسئلة</span>
              <b className="text-white">{mode.questionsCount ?? 0}</b>
            </li>
            {mode.videoQuestionsCount > 0 && (
              <li className="flex justify-between py-2">
                <span>أسئلة فيديو</span>
                <b className="text-white">{mode.videoQuestionsCount}</b>
              </li>
            )}
            {mode.audioQuestionsCount > 0 && (
              <li className="flex justify-between py-2">
                <span>أسئلة صوتية</span>
                <b className="text-white">{mode.audioQuestionsCount}</b>
              </li>
            )}
          </ul>
        )}
        {popup === "media" && (
          <ul className="divide-y divide-white/10 text-white/80">
            <li className="flex justify-between py-2">
              <span>أسئلة فيديو</span>
              <b className="text-white">{mode.videoQuestionsCount || 0}</b>
            </li>
            <li className="flex justify-between py-2">
              <span>أسئلة صوتية</span>
              <b className="text-white">{mode.audioQuestionsCount || 0}</b>
            </li>
          </ul>
        )}
        {popup === "info" && (
          <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{mode.info}</p>
        )}
        <Button block className="mt-5" onClick={() => setPopup(null)}>
          إغلاق
        </Button>
      </Modal>
    </div>
  );
};

export default PublishedModeCard;
